"use client";
import Image from "next/image";
import Link from "next/link";
import {
  Newspaper,
  Pencil,
  Home,
  Store,
  User,
  FileText,
  ExternalLink,
} from "lucide-react";

// Icon mapping for document types
const getDocumentIcon = (title: string) => {
  const titleLower = title.toLowerCase();
  if (titleLower.includes("info") || titleLower.includes("pack"))
    return Newspaper;
  if (titleLower.includes("application") || titleLower.includes("enrol"))
    return Pencil;
  if (titleLower.includes("boarding") || titleLower.includes("accommodation"))
    return Home;
  if (titleLower.includes("cafeteria") || titleLower.includes("food"))
    return Store;
  if (titleLower.includes("parent") || titleLower.includes("agreement"))
    return User;
  return FileText;
};

interface CMSContactInfoProps {
  settings?: {
    phone?: string | null;
    email?: string | null;
    address?: string | null;
    openingHours?: string | null;
    logoUrl?: string | null;
    schoolName?: string | null;
    mapUrlGoogle?: string | null;
    mapUrlApple?: string | null;
  };
  contactInfo?: Record<string, string>;
}

const DOC_KEYS = [
  "info_pack",
  "application",
  "boarding",
  "cafeteria",
  "enrolment",
  "parent",
  "fee_structure",
  "cambridge",
] as const;

export default function CMSContactInfo({
  settings,
  contactInfo = {},
}: CMSContactInfoProps) {
  // Build documents from contact_info blocks
  const documents = DOC_KEYS
    .map((key) => ({
      name: contactInfo[`doc_${key}_label`] ?? "",
      url:  contactInfo[`doc_${key}_url`]   ?? "",
    }))
    .filter((d) => d.name && d.url);

  const phone        = settings?.phone;
  const email        = settings?.email;
  const address      = settings?.address;
  const openingHours = settings?.openingHours;
  const logoUrl      = settings?.logoUrl;
  const specialHours = contactInfo?.special_hours;
  const sectionHeading = contactInfo?.section_heading;
  const docsHeading    = contactInfo?.docs_heading;
  const karriHeading = contactInfo?.karri_heading;
  const karriSubheading = contactInfo?.karri_subheading;
  const karriDescription = contactInfo?.karri_description;
  const karriBtnText = contactInfo?.karri_btn_text;
  const karriLogoUrl = contactInfo?.karri_logo_url;
  const karriBtnUrl  = contactInfo?.karri_btn_url;

  const getMapUrl = () => {
    if (typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent)) {
      return settings?.mapUrlApple ?? "#";
    }
    return settings?.mapUrlGoogle ?? "#";
  };

  return (
    <aside className="bg-white rounded-lg shadow-lg p-8 w-full flex flex-col items-center">
      {logoUrl && (
        <div className="w-full flex justify-center mb-6">
          <Image
            src={logoUrl}
            alt="School Logo"
            width={160}
            height={160}
            sizes="(max-width: 600px) 100vw, 160px"
            priority
            quality={80}
          />
        </div>
      )}

      {sectionHeading && (
        <h2 className="text-2xl font-bold text-school-navy mb-2 text-center">
          {sectionHeading}
        </h2>
      )}

      {phone && <p className="mb-2 text-gray-700 font-semibold text-center">{phone}</p>}

      {email && (
        <p className="mb-4 text-gray-700 text-center">
          <a href={`mailto:${email}`} className="underline text-blue-600">
            {email}
          </a>
        </p>
      )}

      {/* Special Hours */}
      {specialHours && (
        <div className="w-full mb-4 p-3 bg-school-gold/10 rounded-lg">
          <h4 className="font-bold mb-1 text-school-navy">Special Hours Notice</h4>
          <p className="text-gray-700 text-sm">{specialHours}</p>
        </div>
      )}

      {/* Opening Hours */}
      {openingHours && (
        <div className="w-full mb-4 p-3 bg-gray-50 rounded-lg">
          <h3 className="font-bold mb-2 text-school-navy">Opening Hours</h3>
          {openingHours.split("\n").map((line, idx) => (
            <p key={idx} className="text-gray-700 text-sm mb-1">
              {line.includes(":") ? (
                <>
                  {line.split(":")[0]}:{" "}
                  <span className="font-semibold">{line.split(":")[1]}</span>
                </>
              ) : (
                <span className="font-semibold">{line}</span>
              )}
            </p>
          ))}
        </div>
      )}

      {/* Documents */}
      {documents.length > 0 && (
        <div className="w-full mb-4">
          {docsHeading && (
            <h4 className="font-bold mb-4 text-lg">
              <span className="text-black font-extrabold">{docsHeading}</span>
            </h4>
          )}
          <ul className="space-y-3">
            {documents.map((doc, index) => {
              const Icon = getDocumentIcon(doc.name);
              return (
                <li key={index} className="flex items-center gap-3">
                  <Icon className="w-5 h-5 text-school-gold shrink-0" />
                  <a
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-black hover:text-school-gold transition-colors font-medium"
                  >
                    {doc.name}
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* School Location */}
      {address && (
        <div className="w-full mb-6">
          <h3 className="font-bold mb-2">School Location</h3>
          <button
            onClick={() => window.open(getMapUrl(), "_blank", "noopener,noreferrer")}
            className="flex items-center gap-3 w-full p-4 bg-gray-50 hover:bg-school-navy hover:text-white text-school-navy rounded-lg transition-colors duration-200 group cursor-pointer"
            aria-label="Open school location in maps app"
          >
            <span className="text-2xl shrink-0">📍</span>
            <span className="text-sm font-medium leading-snug text-left">{address}</span>
            <ExternalLink className="w-4 h-4 ml-auto shrink-0 opacity-50 group-hover:opacity-100" />
          </button>
        </div>
      )}

      {/* Karri App Section */}
      {(karriHeading || karriDescription || karriBtnText) && (
      <div className="w-full p-6 bg-school-navy rounded-lg border-l-4 border-blue-500 shadow-lg">
        <div className="flex items-center gap-3 mb-4">
          {karriLogoUrl && (
            <Image
              src={karriLogoUrl}
              alt="Karri App Logo"
              width={96}
              height={64}
              quality={80}
            />
          )}
          <div>
            {karriHeading && <h4 className="font-bold text-white text-lg" suppressHydrationWarning>{karriHeading}</h4>}
            {karriSubheading && <p className="text-sm text-gray-300" suppressHydrationWarning>{karriSubheading}</p>}
          </div>
        </div>
        {karriDescription && <p className="text-sm text-gray-300 mb-4" suppressHydrationWarning>{karriDescription}</p>}
        {karriBtnText && karriBtnUrl && (
          <Link
            href={karriBtnUrl}
            className="inline-flex items-center justify-center gap-2 bg-school-gold hover:bg-school-gold-dark text-black font-bold py-3 px-6 rounded-lg transition-colors w-full"
          >
            <ExternalLink className="w-5 h-5" />
            {karriBtnText}
          </Link>
        )}
      </div>
      )}
    </aside>
  );
}
